'use client'

import { C, Callout, Exercise, H, Lesson, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/code-extra'

export default function CodePowerTools() {
  return (
    <Lesson>
      <Step>
        <H>Shorthand you’ll see everywhere</H>
        <P>
          Machine-learning code is full of small bits of shorthand. None of them let you do anything new — but they make code
          much shorter, and you’ll meet them in every example from here on. Let’s learn to read them.
        </P>
        <P>
          <strong>Destructuring</strong> unpacks arrays and objects into separate variables. <strong>Spread</strong> (three dots,{' '}
          <C>...</C>) does the opposite: it unpacks an array <em>into</em> a list of items — handy for copying, combining, and
          passing arrays to functions like <C>Math.max</C> that expect separate numbers.
        </P>
        <Exercise id="destructuring" title="Destructuring, spread and rest" {...EX.destructuring} />
      </Step>

      <Step gate="q-destructure">
        <H>Reading it fluently</H>
        <Quiz
          id="q-destructure"
          question={
            <>
              After <C>{'const [a, , c] = [10, 20, 30]'}</C>, what is <C>c</C>?
            </>
          }
          options={[
            { label: '30', correct: true },
            { label: '20', why: 'The empty slot between the commas skips the second item.' },
            { label: 'undefined', why: 'There are three items; c takes the third.' },
          ]}
          explain="An empty slot in an array pattern skips that position. You’ll see const [x, y] = point, and const { data, grad } = value all over ML code."
        />
      </Step>

      <Step>
        <H>Sorting, ternaries and the Math toolbox</H>
        <P>
          <C>sort</C> is one of the most-used array methods — and one of the most surprising. Plus two tiny tools: the{' '}
          <strong>ternary operator</strong> <C>condition ? a : b</C> (an if/else that produces a value), and the{' '}
          <strong>remainder</strong> operator <C>%</C>.
        </P>
        <Exercise id="sorting" title="Sorting and friends" {...EX.sorting} />
        <Callout kind="warn">
          Two sort traps catch everyone: without a comparator, numbers are sorted as if they were text; and <C>sort</C> rearranges
          the original array. Use <C>{'.slice().sort((a, b) => a - b)'}</C> to sort a copy, numerically.
        </Callout>
      </Step>

      <Step gate="top-k">
        <H>Put them to work</H>
        <P>
          <C>topK</C> is a real function from language models — it picks the k most likely next tokens. You’ll use it again for
          sampling in Module 8.
        </P>
        <Exercise
          id="top-k"
          title="topK, summarise, label"
          {...EX.topK}
          hints={[
            <>
              <C>{'range(scores.length).sort((a, b) => scores[b] - scores[a]).slice(0, k)'}</C> sorts the <em>indices</em> by their
              score.
            </>,
            <>
              <C>Math.min(...xs)</C> and <C>Math.max(...xs)</C> use spread to pass the array’s items as separate arguments.
            </>,
          ]}
        />
      </Step>

      <Step gate="q-types">
        <H>A few more type annotations</H>
        <P>You’ll also see a handful of TypeScript type shapes in examples:</P>
        <UL>
          <li>
            <C>[number, number]</C> — a <strong>tuple</strong>: exactly two numbers, like a 2-D point.
          </li>
          <li>
            <C>{"'train' | 'test'"}</C> — a <strong>union</strong>: one of these specific values.
          </li>
          <li>
            <C>{'(x: number) => number'}</C> — the type of a function.
          </li>
          <li>
            <C>{'function f(x: number, scale = 1)'}</C> — a <strong>default</strong> value used when an argument is left out.
          </li>
        </UL>
        <Quiz
          id="q-types"
          question={
            <>
              What does the type <C>{'(xs: number[]) => number'}</C> describe?
            </>
          }
          options={[
            { label: 'A function that takes a list of numbers and returns one number', correct: true },
            { label: 'An array of functions', why: 'The arrow => marks a function type: inputs on the left, output on the right.' },
            { label: 'A number that can also be an array', why: 'That would be number | number[] (a union).' },
          ]}
          explain="vec.sum, vec.mean and vec.max all have exactly this type."
        />
      </Step>
    </Lesson>
  )
}
